import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence } from '../common/ViewingTemplate';
import GanttChart from './GanttChart';
import PdfViewer from './PdfViewer';
import ScheduleUpdateModal from './ScheduleUpdateModal';

const ACTIVITY_LAYOUT_COLUMNS = [
  { key: 'comments', label: 'Comments' },
  { key: 'activity_id', label: 'Activity ID' },
  { key: 'activity_name', label: 'Activity Name' },
  { key: 'resp_party', label: 'Resp Party', defaultHidden: true },
  { key: 'upd_resp', label: 'Upd Resp', defaultHidden: true },
  { key: 'resource_id_name', label: 'Resource Name', defaultHidden: true },
  { key: 'duration', label: 'Duration', defaultHidden: true },
  { key: 'remaining_duration', label: 'Remaining Duration', defaultHidden: true },
  { key: 'percent_complete', label: '%' },
  { key: 'start_date', label: 'Start' },
  { key: 'finish_date', label: 'Finish' },
  { key: 'activity_status', label: 'Activity Status', defaultHidden: true },
];

const ScheduleInstances = ({ jobNumber, projectId }) => {
  const { instance, accounts } = useMsal();
  const excelFileInputRef = useRef(null);
  const pdfFileInputRef = useRef(null);

  // Live data push: ref + callback for real-time refresh
  const fetchDataRef = useRef(null);

  const handleDataChange = useCallback(() => {
    if (fetchDataRef.current) fetchDataRef.current();
  }, []);

  // Real-time presence tracking
  const { socketId } = usePresence('scheduleInstances', projectId || 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);
  
  // State
  const [data, setData] = useState([]);
  const [notes, setNotes] = useState([]);
  const [, setDateColumns] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importingPdf, setImportingPdf] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [viewMode, setViewMode] = useState('gantt'); // 'gantt' or 'pdf'
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [currentLayout, setCurrentLayout] = useState('activity'); // 'resource' | 'activity'
  const [versions, setVersions] = useState([]);
  const [showVersionPanel, setShowVersionPanel] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);
  const [editingVersionId, setEditingVersionId] = useState(null);
  const [editingVersionLabel, setEditingVersionLabel] = useState('');
  const [canEditSchedule, setCanEditSchedule] = useState(true);
  const [viewerEmployeeCode, setViewerEmployeeCode] = useState(null);
  const [viewerOrgTags, setViewerOrgTags] = useState([]);
  const [dataDate, setDataDate] = useState(null);

  useEffect(() => {
    if (!accounts[0]) return;
    (async () => {
      try {
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info');
        if (response.data?.success) {
          setCanEditSchedule(response.data.data?.canEditSchedule !== false);
        }
      } catch (err) {
        console.error('Error loading schedule permissions:', err);
      }
    })();
  }, [instance, accounts]);

  // Fetch schedule data
  const fetchData = useCallback(async () => {
    if (!jobNumber) return;
    
    try {
      setLoading(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/schedule-baseline/${jobNumber}`
      );
      
      if (response.data.success) {
        setData(response.data.data || []);
        setNotes(response.data.notes || []);
        setDateColumns(response.data.dateColumns || []);
        setLastUpdated(response.data.lastUpdated);
        setPdfInfo(response.data.pdfInfo || null);
        setViewerEmployeeCode(response.data.viewerEmployeeCode || null);
        setViewerOrgTags(response.data.viewerOrgTags || []);
        setDataDate(response.data.dataDate || null);
      }
    } catch (err) {
      console.error('Error fetching schedule data:', err);
      setError('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  // Fetch PDF info
  const fetchPdfInfo = useCallback(async () => {
    if (!jobNumber) return;
    
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/schedule-baseline/${jobNumber}/pdf/info`
      );
      
      if (response.data.success && response.data.data) {
        setPdfInfo(response.data.data);
      }
    } catch (err) {
      // PDF not found is ok
      console.log('No PDF found for this schedule');
    }
  }, [instance, accounts, jobNumber]);

  const fetchVersions = useCallback(async () => {
    if (!jobNumber) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/versions`);
      if (response.data.success) setVersions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching schedule versions:', err);
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => {
    fetchData();
    fetchPdfInfo();
    fetchVersions();
  }, [fetchData, fetchPdfInfo, fetchVersions]);

  const handleSaveAsVersion = async () => {
    const label = window.prompt('Version label (optional):', '');
    if (label === null) return;
    try {
      setSavingVersion(true);
      setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'POST',
        `/schedule-baseline/${jobNumber}/versions/snapshot`,
        { label: label || undefined }, syncHeaders());
      if (response.data.success) {
        setSuccessMessage(`Version ${response.data.data.version_number} saved`);
        setTimeout(() => setSuccessMessage(null), 3000);
        fetchVersions();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save version');
    } finally {
      setSavingVersion(false);
    }
  };

  const handleUpdateVersionLabel = async (versionId, newLabel) => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT',
        `/schedule-baseline/${jobNumber}/versions/${versionId}`,
        { label: newLabel }, syncHeaders());
      setVersions(prev => prev.map(v => v.id === versionId ? { ...v, label: newLabel } : v));
      setEditingVersionId(null);
    } catch (err) {
      setError('Failed to update version label');
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm('Delete this version? This cannot be undone.')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE',
        `/schedule-baseline/${jobNumber}/versions/${versionId}`, null, syncHeaders());
      setVersions(prev => prev.filter(v => v.id !== versionId));
    } catch (err) {
      setError('Failed to delete version');
    }
  };

  // Handle Excel file import
  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setImporting(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/schedule-baseline/${jobNumber}/import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data', ...(syncHeaders().headers || {}) } }
      );
      
      if (response.data.success) {
        const vMsg = response.data.versionCreated ? ` (previous data saved as v${response.data.versionCreated.version_number})` : '';
        setSuccessMessage(`Successfully imported ${response.data.count} activities${vMsg}`);
        fetchData();
        fetchVersions();
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(response.data.error || 'Import failed');
      }
    } catch (err) {
      console.error('Error importing file:', err);
      setError('Failed to import file: ' + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
      if (excelFileInputRef.current) {
        excelFileInputRef.current.value = '';
      }
    }
  };

  // Handle PDF file import
  const handleImportPdf = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setImportingPdf(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/schedule-baseline/${jobNumber}/pdf`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data', ...(syncHeaders().headers || {}) } }
      );
      
      if (response.data.success) {
        setSuccessMessage('PDF uploaded successfully');
        setPdfInfo(response.data.data);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.data.error || 'PDF upload failed');
      }
    } catch (err) {
      console.error('Error uploading PDF:', err);
      setError('Failed to upload PDF: ' + (err.response?.data?.error || err.message));
    } finally {
      setImportingPdf(false);
      if (pdfFileInputRef.current) {
        pdfFileInputRef.current.value = '';
      }
    }
  };

  // Handle Excel export
  const handleExportExcel = async () => {
    try {
      setExporting(true);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/schedule-baseline/${jobNumber}/export/excel`,
        null,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `schedule_baseline_${jobNumber}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      setError('Failed to export Excel file');
    } finally {
      setExporting(false);
    }
  };

  // Handle PDF export (uses browser print)
  const handleExportPDF = () => {
    window.print();
  };

  // Toggle between Gantt and PDF view
  const toggleView = () => {
    if (viewMode === 'gantt') {
      // Switch to PDF view
      if (!pdfInfo) {
        setError('No PDF has been uploaded for this schedule');
        return;
      }
      // Use direct URL to backend - no XHR, no CORS issues
      const directPdfUrl = `${window.location.protocol}//${window.location.hostname}:3003/api/schedule-baseline/${jobNumber}/pdf`;
      setPdfUrl(directPdfUrl);
      setViewMode('pdf');
    } else {
      // Switch back to Gantt view
      setViewMode('gantt');
    }
  };

  // Add schedule note
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/schedule-baseline/${jobNumber}/notes`,
        { noteText: newNote },
        syncHeaders()
      );
      
      if (response.data.success) {
        setNotes([response.data.data, ...notes]);
        setNewNote('');
        setShowNotesModal(false);
      }
    } catch (err) {
      console.error('Error adding note:', err);
      setError('Failed to add note');
    }
  };

  // Delete schedule note
  const handleDeleteNote = async (noteId) => {
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/schedule-baseline/${jobNumber}/notes/${noteId}`,
        null,
        syncHeaders()
      );
      
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  // Update activity note (passed to GanttChart)
  const handleUpdateActivityNote = async (activityId, noteText) => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/schedule-baseline/${jobNumber}/activity/${activityId}/notes`,
        { notes: noteText },
        syncHeaders()
      );
      
      if (response.data.success) {
        const updatedRow = response.data.data;
        const userName = accounts?.[0]?.name || accounts?.[0]?.username || '';
        setData(prev => prev.map(row => 
          row.id.toString() === activityId.toString() 
            ? { ...row, notes: noteText, updated_by: updatedRow?.updated_by || row.updated_by, updated_at: updatedRow?.updated_at || new Date().toISOString(), note_updated_by_name: userName } 
            : row
        ));
      }
    } catch (err) {
      console.error('Error updating activity note:', err);
      throw err;
    }
  };

  // Update activity progress fields (passed to GanttChart)
  const handleUpdateActivityProgress = async (activityId, payload) => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/schedule-baseline/${jobNumber}/activity/${activityId}/progress`,
        payload,
        syncHeaders()
      );
      
      if (response.data.success) {
        setData(prev => prev.map(row => 
          row.id.toString() === activityId.toString() 
            ? { ...row, ...payload }
            : row
        ));
      }
    } catch (err) {
      console.error('Error updating activity progress:', err);
      throw err;
    }
  };

  // Format date for display — reserved for future table mode
  // eslint-disable-next-line no-unused-vars
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // eslint-disable-next-line no-unused-vars
  const formatLastUpdated = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const grayBtnStyle = {
    padding: '0.5rem 1rem',
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    transition: 'all 0.15s ease'
  };
  const menuItemStyle = (disabled, active) => ({
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '7px 10px',
    border: 'none',
    borderRadius: 4,
    background: active ? '#e0f2fe' : 'transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.82rem',
    color: disabled ? '#9ca3af' : '#1f2937',
    fontWeight: active ? 650 : 450,
  });

  const fileMenu = (
    <div>
      <input type="file" ref={excelFileInputRef} onChange={handleImportExcel} accept=".xlsx,.xls" style={{ display: 'none' }} />
      <input type="file" ref={pdfFileInputRef} onChange={handleImportPdf} accept=".pdf" style={{ display: 'none' }} />
      {canEditSchedule && (
        <>
          <button type="button" disabled={importing} onClick={() => excelFileInputRef.current?.click()} style={menuItemStyle(importing)}>{importing ? 'Importing Excel…' : 'Import Excel'}</button>
          <button type="button" disabled={importingPdf} onClick={() => pdfFileInputRef.current?.click()} style={menuItemStyle(importingPdf)}>{importingPdf ? 'Uploading PDF…' : 'Import PDF'}</button>
        </>
      )}
      <button type="button" disabled={exporting || data.length === 0} onClick={handleExportExcel} style={menuItemStyle(exporting || data.length === 0)}>Export Excel</button>
      <button type="button" disabled={data.length === 0} onClick={handleExportPDF} style={menuItemStyle(data.length === 0)}>Export PDF</button>
      <button type="button" disabled={viewMode === 'gantt' && !pdfInfo} onClick={toggleView} title={pdfInfo ? pdfInfo.filename : 'No PDF uploaded'} style={menuItemStyle(viewMode === 'gantt' && !pdfInfo)}>{viewMode === 'pdf' ? 'Show schedule' : 'Show PDF'}</button>
      <button type="button" onClick={() => setShowNotesModal(true)} style={menuItemStyle(false)}>Notes ({notes.length})</button>
      {canEditSchedule && (
        <>
          <button type="button" onClick={() => setShowUpdateModal(true)} style={menuItemStyle(false)}>Update reminder</button>
          <button type="button" disabled={savingVersion || data.length === 0} onClick={handleSaveAsVersion} style={menuItemStyle(savingVersion || data.length === 0)}>{savingVersion ? 'Saving version…' : 'Save version'}</button>
        </>
      )}
      <button type="button" onClick={() => { setShowVersionPanel(!showVersionPanel); if (!showVersionPanel) fetchVersions(); }} style={menuItemStyle(false, showVersionPanel)}>Versions{versions.length > 0 ? ` (${versions.length})` : ''}</button>
      <div style={{ height: 1, background: '#e5e7eb', margin: '6px 4px' }} />
      <button type="button" onClick={() => setCurrentLayout('activity')} style={menuItemStyle(false, currentLayout === 'activity')}>Activity layout</button>
      <button type="button" onClick={() => setCurrentLayout('resource')} style={menuItemStyle(false, currentLayout === 'resource')}>Resource layout</button>
    </div>
  );

  return (
    <div style={{ padding: 0 }}>

      {/* Messages */}
      {error && (
        <div style={{
          padding: '0.75rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          ❌ {error}
          <button 
            onClick={() => setError(null)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}
      
      {successMessage && (
        <div style={{
          padding: '0.75rem',
          backgroundColor: '#d4edda',
          color: '#155724',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          ✅ {successMessage}
        </div>
      )}

      {/* Version History Panel */}
      {showVersionPanel && (
        <div style={{
          marginBottom: '1rem',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          backgroundColor: '#f9fafb',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>🕒 Schedule Version History</h3>
            <button onClick={() => setShowVersionPanel(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {versions.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
                No versions saved yet. Versions are automatically created when you import a new schedule, or click "Save Version" to create one manually.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Version</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Label</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Source</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600 }}>Total Hours</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Created</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>By</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map(v => (
                    <tr key={v.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        v{v.version_number}
                        {v.is_baseline && <span style={{ marginLeft: '0.5rem', backgroundColor: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600 }}>BASELINE</span>}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        {canEditSchedule && editingVersionId === v.id ? (
                          <span style={{ display: 'flex', gap: '4px' }}>
                            <input
                              value={editingVersionLabel}
                              onChange={e => setEditingVersionLabel(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') handleUpdateVersionLabel(v.id, editingVersionLabel); if (e.key === 'Escape') setEditingVersionId(null); }}
                              autoFocus
                              style={{ padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', width: '160px' }}
                            />
                            <button onClick={() => handleUpdateVersionLabel(v.id, editingVersionLabel)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>✓</button>
                            <button onClick={() => setEditingVersionId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>✕</button>
                          </span>
                        ) : (
                          <span onClick={canEditSchedule ? () => { setEditingVersionId(v.id); setEditingVersionLabel(v.label || ''); } : undefined} style={{ cursor: canEditSchedule ? 'pointer' : 'default', borderBottom: canEditSchedule ? '1px dashed #9ca3af' : 'none' }} title={canEditSchedule ? 'Click to edit' : undefined}>
                            {v.label || '-'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', textTransform: 'capitalize' }}>{v.source}</td>
                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{parseFloat(v.total_hours || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        {v.created_at ? new Date(v.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' }) : '-'}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{v.created_by_name || v.created_by || '-'}</td>
                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                        {canEditSchedule && <button onClick={() => handleDeleteVersion(v.id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '0.85rem' }} title="Delete version">🗑️</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* P6-Style Integrated Gantt Chart Layout OR PDF View */}
      {viewMode === 'gantt' ? (
        <GanttChart 
          jobNumber={jobNumber}
          data={data}
          onUpdateActivityNote={canEditSchedule ? handleUpdateActivityNote : undefined}
          onUpdateActivityProgress={canEditSchedule ? handleUpdateActivityProgress : undefined}
          readOnly={!canEditSchedule}
          loading={loading}
          currentUserName={accounts?.[0]?.name || accounts?.[0]?.username}
          lastUpdated={lastUpdated}
          layout={currentLayout}
          activityLayoutColumns={ACTIVITY_LAYOUT_COLUMNS}
          projectId={projectId}
          viewerEmployeeCode={viewerEmployeeCode}
          viewerOrgTags={viewerOrgTags}
          dataDate={dataDate}
          fileMenu={fileMenu}
        />
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button type="button" onClick={toggleView} style={grayBtnStyle}>Show schedule</button>
          </div>
          <div style={{
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: 'white',
          height: '600px'
        }}>
          {pdfUrl ? (
            <PdfViewer 
              pdfUrl={pdfUrl}
              filename={pdfInfo?.filename}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#6c757d'
            }}>
              Loading PDF...
            </div>
          )}
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', width: '90%', maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>📝 Schedule Notes</h3>
              <button onClick={() => setShowNotesModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
            </div>
            
            {/* Add new note with optional activity reference */}
            {canEditSchedule && <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input type="text" placeholder="Activity ID (optional)" id="note-activity-id" style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #dee2e6', fontSize: '0.85rem', width: '140px' }} />
                <input type="text" placeholder="Activity Name (optional)" id="note-activity-name" style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #dee2e6', fontSize: '0.85rem', flex: 1 }} />
              </div>
              <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a new note..." style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #dee2e6', minHeight: '80px', resize: 'vertical' }} />
              <button
                onClick={() => {
                  const actId = document.getElementById('note-activity-id')?.value || '';
                  const actName = document.getElementById('note-activity-name')?.value || '';
                  const prefix = actId || actName ? `[${actId}${actId && actName ? ' - ' : ''}${actName}] ` : '';
                  const fullNote = prefix + newNote;
                  if (!fullNote.trim()) return;
                  setNewNote(fullNote);
                  handleAddNote();
                }}
                disabled={!newNote.trim()}
                style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: newNote.trim() ? '#28a745' : '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: newNote.trim() ? 'pointer' : 'not-allowed' }}
              >Add Note</button>
            </div>}
            
            {/* Export activity notes */}
            {data.length > 0 && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#e8f5e9', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: '#2e7d32' }}>Activity notes from the Gantt chart can be included in Excel export.</span>
                <button onClick={handleExportExcel} style={{ ...grayBtnStyle, fontSize: '0.8rem', padding: '4px 12px' }}>📤 Export Excel (with notes)</button>
              </div>
            )}
            
            {/* Existing notes */}
            {notes.length === 0 ? (
              <p style={{ color: '#6c757d', textAlign: 'center' }}>No notes yet. Add one above.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {notes.map(note => (
                  <div key={note.id} style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        <span style={{ fontWeight: 600, color: '#374151' }}>{note.created_by || 'Unknown'}</span>
                        <span> — </span>
                        <span>{note.created_at ? new Date(note.created_at).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago', timeZoneName: 'short' }) : '-'}</span>
                        {note.activity_id && <span> | Activity: {note.activity_id}</span>}
                        {note.activity_name && <span> ({note.activity_name})</span>}
                      </div>
                      {canEditSchedule && <button onClick={() => handleDeleteNote(note.id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '0.8rem' }}>🗑️</button>}
                    </div>
                    <p style={{ margin: 0 }}>{note.note_text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Update Settings Modal */}
      <ScheduleUpdateModal isOpen={showUpdateModal} onClose={() => setShowUpdateModal(false)} jobNumber={jobNumber} />

      {/* Print styles */}
      <style>{`
        @media print {
          button, input[type="file"] {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ScheduleInstances;
